import React from 'react';
import { Controller, type Control } from 'react-hook-form';
import { Input, Label, Select } from '@shared/components';
import type {
    SmriqPlanFormInput,
    SmriqPlanFormOutput,
} from '../../../../schemas/smriqJourney.schema';
import { BENEATH_TITLE, TITLED_COLUMN } from './fieldLayout';

interface DurationFieldProps {
    control: Control<SmriqPlanFormInput, unknown, SmriqPlanFormOutput>;
    lockedMonths: number | null;
    durationOptions?: number[] | null;
    fieldsDisabled: boolean;
    onFieldsChange: () => void;
}

export const DurationField: React.FC<DurationFieldProps> = ({
    control,
    lockedMonths,
    durationOptions,
    fieldsDisabled,
    onFieldsChange,
}) => {
    if (lockedMonths !== null) {
        return (
            <div className={TITLED_COLUMN}>
                <Label>Duration (months)</Label>
                <div className={BENEATH_TITLE}>
                    <p className="text-sm text-mp-foreground">
                        {lockedMonths} months
                    </p>
                </div>
            </div>
        );
    }

    const hasDurationOptions = Array.isArray(durationOptions) && durationOptions.length > 0;

    return (
        <div>
            <Label htmlFor="smriq_duration_months" required>
                Duration (months)
            </Label>
            <Controller
                name="duration_months"
                control={control}
                render={({ field, fieldState: { error: fe } }) =>
                    hasDurationOptions ? (
                        <Select
                            id="smriq_duration_months"
                            ariaLabel="Duration (months)"
                            placeholder="Select a duration"
                            options={durationOptions.map((months) => ({
                                value: String(months),
                                label: `${months} months`,
                            }))}
                            value={field.value}
                            disabled={fieldsDisabled}
                            error={!!fe}
                            hint={fe?.message}
                            ariaDescribedBy={fe ? 'smriq_duration_months-hint' : undefined}
                            onChange={(next) => {
                                field.onChange(next);
                                onFieldsChange();
                            }}
                            onBlur={field.onBlur}
                        />
                    ) : (
                        <Input
                            id="smriq_duration_months"
                            type="number"
                            inputMode="numeric"
                            disabled={fieldsDisabled}
                            value={field.value}
                            onChange={(e) => {
                                field.onChange(e);
                                onFieldsChange();
                            }}
                            onBlur={field.onBlur}
                            error={!!fe}
                            hint={fe?.message}
                            hintId="smriq_duration_months-hint"
                            aria-invalid={!!fe}
                            aria-required
                            aria-describedby={fe ? 'smriq_duration_months-hint' : undefined}
                        />
                    )
                }
            />
        </div>
    );
};
